import { Component, Inject } from '@angular/core';
import { MatDialogRef, MatDialogModule, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { GenericListComponent } from '../../shared/generic-list/generic-list.component';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { ColumnDefinition } from '../../../models/columnDefinition';
import { CommonModule } from '@angular/common';
import { CompanySearchBoxComponent, CompanySearchFieldsConfig } from '../../companies/company-search-box/company-search-box.component';
import { CompanySearchFilter } from '../../../models/companySearchFilter';
import { CompanyService } from '../../../services/companies/company.service';
import { MatChip, MatChipSet } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { CompanyDto } from '../../../models/api/companyDto';
import { CompanySearchRequest } from '../../../models/api/companySearchRequest';

@Component({
  selector: 'app-company-selection-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    GenericListComponent,
    CommonModule,
    CompanySearchBoxComponent,
    MatChipSet,
    MatChip,
    MatIcon,
    MatExpansionModule],
  templateUrl: './company-selection-dialog.component.html',
  styleUrls: ['./company-selection-dialog.component.scss']
})
export class CompanySelectionDialogComponent {
  companies: CompanyDto[] = [];
  totalItems = 0;
  currentPage = 0;
  pageSize = 10;

  fieldsConfig: CompanySearchFieldsConfig = {
    showMeasurement: false,
    showCountry: true,
    showListingStatus: false,
    showIndex: false,
    showName: true,
    showIndustry: false,
    showUnitaryDual: false,
    showCapitalization: false,
    showIsEmpty: false,
    showCategory: false,
    showFinished: false,
    showChecked: false,
    showIsin: false,
    showIsActive: false
  };

  columnDefinitions: ColumnDefinition[] = [
    { propertyName: 'companyName', displayName: 'Name' },
    { propertyName: 'year', displayName: 'Year' },
    { propertyName: 'countryName', displayName: 'Country' },
    { propertyName: 'address', displayName: 'Address' },
  ];

  selectedCompanies: CompanyDto[] = [];

  private searchFilter: CompanySearchFilter = {};
  private sortBy?: string;
  private ascending = true;

  constructor(
    public dialogRef: MatDialogRef<CompanySelectionDialogComponent>,
    private companyService: CompanyService,
    @Inject(MAT_DIALOG_DATA) data: { selectedCompanies?: CompanyDto[] } | null
  ) {
    this.selectedCompanies = data?.selectedCompanies ? [...data.selectedCompanies] : [];
    this.loadCompanies();
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  onConfirm(): void {
    this.dialogRef.close(this.selectedCompanies);
  }

  onItemClick(item: CompanyDto): void {
    if (!this.selectedCompanies.some(c => c.id === item.id)) {
      this.selectedCompanies.push(item);
    }
  }

  isCompanySelected = (company: CompanyDto): boolean => {
    return this.selectedCompanies.some(c => c.id === company.id);
  };

  onSortChange(sort: Sort): void {
    this.sortBy = sort.active;
    this.ascending = sort.direction === 'asc';
    this.currentPage = 0;
    this.loadCompanies();
  }

  onPageChange(page: PageEvent): void {
    this.currentPage = page.pageIndex;
    this.pageSize = page.pageSize;
    this.loadCompanies();
  }

  onCompanySearchFilterChange(companySearchFilter: CompanySearchFilter): void {
    this.searchFilter = companySearchFilter;
    this.currentPage = 0;
    this.loadCompanies();
  }

  removeCompany(index: number): void {
    this.selectedCompanies.splice(index, 1);
  }

  private loadCompanies(): void {
    const searchRequest: CompanySearchRequest = {
      pageNumber: this.currentPage + 1,
      pageSize: this.pageSize,
      sortBy: this.sortBy,
      ascending: this.ascending,
      countryId: this.searchFilter.country,
      companyName: this.searchFilter.name,
      year: undefined
    };

    this.companyService.search(searchRequest).subscribe({
      next: (response) => {
        this.companies = response.data;
        this.totalItems = response.totalCount;
        this.currentPage = response.page - 1;
      },
      error: (error) => {
        console.error('Error loading companies:', error);
      }
    });
  }
}
